import React, { useEffect } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import styles from '../../styles/Tabs.module.css';
import DurationInput from '../inputs/DurationInput';
import FlightLocationInput from '../inputs/flight/FlightLocationInput';
import { airports } from '../../utils/iataPorts';
import { CALCULATOR_MODE } from '../../@types/travel';
import { AppButton } from '../app-button';
import { airportsClasses } from '../../utils/tabsConfig';
import Select from '../Select';
import Counter from '../inputs/Counter';
import { ErrorMessage } from './error-message';
import { FlightModeNavigation } from './flight-mode-navigation';
import { IconButton } from '../icon-button';
import { CloseIcon } from '../../utils/icons';

//
//

const FLIGHTS_MULTI_CITY_LIMIT = 7;

//

type TabContentProps = {
  useTravelTime?: boolean;
  ModeSwitcher: React.ReactElement;
  calculatorMode: string;
  setCalculatorMode: (mode: string) => void;
  setDisplay2Row: (flag: boolean) => void;
  display2Row: boolean;
};

export const FlightTabContent: React.FC<TabContentProps> = ({
  useTravelTime,
  ModeSwitcher,
  calculatorMode,
  setCalculatorMode,
  display2Row,
  setDisplay2Row,
}) => {
  const { watch, setValue, register } = useFormContext();

  const flightsPassengers = watch('flights.passengers');
  const flightsCabinClass = watch('flights.cabinClass');
  const flightsDuration = watch('flights.duration');

  const flightError = watch('flights.errorMessage');

  useEffect(() => {
    setValue('flights.errorMessage', '');
  }, [calculatorMode, setValue]);

  //

  return (
    <div className={styles.tabContent}>
      {/* Move to the top lvl, imposable now due to affecting other tab panels */}
      {!useTravelTime && <ErrorMessage message={flightError} />}

      {useTravelTime ? (
        <DurationInput
          label='How long did the flight take? (Hours, one way)'
          value={flightsDuration}
          onChange={(value) => setValue('flights.duration', value)}
          minHours={1}
          maxHours={15}
          rightCorner={ModeSwitcher}
        />
      ) : (
        <FlightsWrapper
          isMultiCity={calculatorMode === CALCULATOR_MODE.multicity}
          calculatorMode={calculatorMode}
          setDisplay2Row={setDisplay2Row}
        />
      )}

      {!display2Row && !useTravelTime && ModeSwitcher}

      {display2Row && (
        <div className={`${styles.tabContentAppearing} ${styles.fieldRow}`}>
          <Counter
            onChange={(value) => setValue('flights.passengers', value)}
            value={flightsPassengers}
            label='Number of passengers'
          />
          <Select
            onChange={(value) => setValue('flights.cabinClass', value)}
            id='flights.cabinClass'
            label='Class of travel'
            optionsList={airportsClasses}
            register={register}
            selectedItem={flightsCabinClass}
          />
          <FlightModeNavigation
            calculatorMode={calculatorMode}
            setCalculatorMode={setCalculatorMode}
            useTravelTime={useTravelTime}
          />
        </div>
      )}
    </div>
  );
};

//
//

type FlightLocationInputRowProps = {
  originId: string;
  destinationId: string;
  onOriginChange: (val: string) => void;
  onDestinationChange: (val: string) => void;
  setDisplay2Row: (flag: boolean) => void;
};

const FlightLocationInputRow: React.FC<FlightLocationInputRowProps> = ({
  originId,
  destinationId,
  onOriginChange,
  onDestinationChange,
  setDisplay2Row,
}) => {
  const { watch, setValue } = useFormContext();
  const flightsFromWatch = watch(originId);
  const flightsToWatch = watch(destinationId);

  useEffect(() => {
    if (flightsFromWatch && flightsToWatch && flightsFromWatch === flightsToWatch) {
      setValue('flights.errorMessage', 'Please, make sure that origin and destination are different.');
      setDisplay2Row && setDisplay2Row(false);
    } else {
      if (flightsFromWatch && flightsToWatch) {
        setDisplay2Row && setDisplay2Row(true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flightsFromWatch, flightsToWatch]);

  //

  return (
    <div className={styles.fieldRow}>
      <FlightLocationInput
        label='From'
        placeholder='Departure'
        optionsData={airports}
        id={originId}
        onChange={onOriginChange}
        selectedItem={flightsFromWatch}
      />
      <FlightLocationInput
        label='To'
        placeholder='Destination'
        optionsData={airports}
        id={destinationId}
        onChange={onDestinationChange}
        selectedItem={flightsToWatch}
      />
    </div>
  );
};

//

type FlightsWrapperProps = {
  isMultiCity: boolean;
  calculatorMode: string;
  setDisplay2Row: (flag: boolean) => void;
};

const FlightsWrapper: React.FC<FlightsWrapperProps> = ({ isMultiCity, calculatorMode, setDisplay2Row }) => {
  const { watch, setValue } = useFormContext();
  const flightsCabinClass = watch('flights.cabinClass');
  const flightsWatch = watch('flights.values');

  const { fields, remove } = useFieldArray({
    control: useFormContext().control,
    name: 'flights.values',
  });

  if (!isMultiCity) {
    return (
      <FlightLocationInputRow
        originId='flights.values.0.origin'
        destinationId='flights.values.0.destination'
        onOriginChange={(value) => setValue('flights.values.0.origin', value)}
        onDestinationChange={(value) => setValue('flights.values.0.destination', value)}
        setDisplay2Row={setDisplay2Row}
      />
    );
  }

  //

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {fields.map((flight, index) => (
        <div key={flight.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <FlightLocationInputRow
            setDisplay2Row={setDisplay2Row}
            originId={`flights.values.${index}.origin`}
            destinationId={`flights.values.${index}.destination`}
            onOriginChange={(value) => setValue(`flights.values.${index}.origin`, value)}
            onDestinationChange={(value) => setValue(`flights.values.${index}.destination`, value)}
          />

          <div style={{ width: 30, display: fields?.length > 1 ? 'block' : 'none' }}>
            {index > 0 && (
              <IconButton icon={<CloseIcon />} onClick={() => remove(index)} style={{ marginTop: '1rem' }} />
            )}
          </div>
        </div>
      ))}

      {calculatorMode === CALCULATOR_MODE.multicity && fields?.length <= FLIGHTS_MULTI_CITY_LIMIT && (
        <AppButton
          variant='text'
          size='small'
          onClick={() =>
            setValue('flights.values', [
              ...flightsWatch,
              {
                origin: flightsWatch[flightsWatch.length - 1].destination || '',
                destination: '',
                cabinClass: flightsCabinClass.value,
              },
            ])
          }
        >
          Add flight
        </AppButton>
      )}
    </div>
  );
};
